/**
 * useCSVParser Hook
 *
 * Manages CSV file parsing and validation state.
 * Handles file upload, parsing, and validation workflow.
 *
 * @module ImportExportTab/hooks/useCSVParser
 */

import { useState, useCallback } from 'react';
import { parseCSVFile } from '../services/csvParser';
import { validateOrgUnits, validateLocations } from '../services/csvValidator';

/**
 * Custom hook for managing CSV parsing and validation
 *
 * Provides state management and execution logic for parsing
 * and validating CSV files.
 *
 * @param {Object[]} existingOrgUnits - Existing OrgUnits from database
 * @param {Object[]} existingLocations - Existing Locations from database
 * @returns {Object} Parse state and functions
 * @returns {Object} return.parseState - Current parse state
 * @returns {boolean} return.parseState.loading - Parse in progress
 * @returns {string|null} return.parseState.error - Error message if parse failed
 * @returns {Object|null} return.parseState.validationResult - Validation results
 * @returns {Function} return.parseFile - Function to parse and validate file
 * @returns {Function} return.resetParse - Function to reset parse state
 *
 * @example
 * const { parseState, parseFile, resetParse } = useCSVParser(existingOrgUnits, existingLocations);
 *
 * const handleFileUpload = async (file) => {
 *   await parseFile(file, 'orgUnits');
 * };
 */
export default function useCSVParser(existingOrgUnits = [], existingLocations = []) {
  const [parseState, setParseState] = useState({
    loading: false,
    error: null,
    validationResult: null
  });

  /**
   * Parse and validate CSV file
   *
   * @param {File} file - File object from input
   * @param {string} importType - Type of import ("orgUnits" or "locations")
   * @returns {Promise<void>}
   */
  const parseFile = useCallback(async (file, importType) => {
    // Set loading state
    setParseState({
      loading: true,
      error: null,
      validationResult: null
    });

    try {
      // Parse CSV file
      const parseResult = await parseCSVFile(file);

      // Check for parse errors
      if (parseResult.errors && parseResult.errors.length > 0) {
        const errorMsg = parseResult.errors[0].message || 'CSV parsing error';
        throw new Error(`Failed to parse CSV file: ${errorMsg}`);
      }

      // Check if we have data
      if (!parseResult.data || parseResult.data.length === 0) {
        throw new Error('CSV file is empty or contains only headers');
      }

      // Validate records based on import type
      let validationResult;
      if (importType === 'orgUnits') {
        validationResult = validateOrgUnits(parseResult.data, existingOrgUnits);
      } else {
        validationResult = validateLocations(parseResult.data, existingLocations, existingOrgUnits);
      }

      // Update state with validation results
      setParseState({
        loading: false,
        error: null,
        validationResult
      });
    } catch (error) {
      // Update state with error
      const errorMessage = error?.message || String(error);
      setParseState({
        loading: false,
        error: errorMessage,
        validationResult: null
      });
    }
  }, [existingOrgUnits, existingLocations]);

  /**
   * Reset parse state
   *
   * Clears loading, error, and validation results.
   * Use this to clear state before a new parse or to dismiss results.
   */
  const resetParse = useCallback(() => {
    setParseState({
      loading: false,
      error: null,
      validationResult: null
    });
  }, []);

  return {
    parseState,
    parseFile,
    resetParse
  };
}
